/**
 * DataQualityCard — props-driven sidebar card for the accountant dashboard.
 *
 * Receives the data_quality slice from /api/v1/dashboard/accountant-payload.
 * This is intentionally separate from DataQualityWidget (dashboard/DataQualityWidget.tsx)
 * which self-fetches and is used on the DataSpecialistDashboard. This card is
 * pure-presentation: parent owns the fetch.
 *
 * Displays:
 *   - Overall quality score + letter grade
 *   - Docs received / required
 *   - Reconciliation % (categories approved)
 *   - Unresolved variances count
 *   - Missing document type names (collapsed by default)
 */

import React, { useState } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  FileText,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import EmptyState from './EmptyState';

interface DataQualitySlice {
  score: number;
  grade: string;
  document_completeness: number;
  documents_received: number;
  documents_required: number;
  missing_doc_types: string[];
  categories_total: number;
  categories_reconciled: number;
  reconciliation_pct: number;
  unresolved_variances: number;
  variance_score: number;
}

interface DataQualityCardProps {
  data?: DataQualitySlice | { error: string } | null;
  isLoading?: boolean;
}

function gradeColor(grade: string): { bg: string; text: string; border: string } {
  // Hueless neutral for all grades (verification state, not action)
  return { bg: 'var(--color-bg-elevated)', text: 'var(--color-text-secondary)', border: 'var(--color-border)' };
}

function scoreBarColor(_score: number): string {
  // Informational meter: the WIDTH carries the score; the fill stays hueless
  // (verified/neutral = no hue; orange is reserved for actions, crimson for blockers).
  return 'var(--color-text-secondary)';
}

export default function DataQualityCard({ data, isLoading }: DataQualityCardProps) {
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return (
      <div className="rounded-xl shadow-sm p-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <ShieldCheck className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Data Quality</h3>
        </div>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin mr-2" style={{ color: 'var(--color-text-dim)' }} />
          <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>Loading…</span>
        </div>
      </div>
    );
  }

  if (!data || 'error' in data) {
    return (
      <div className="rounded-xl shadow-sm p-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <ShieldAlert className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Data Quality</h3>
        </div>
        <EmptyState icon={ShieldAlert} label="No quality data yet" hint="Populates after first reconciliation" />
      </div>
    );
  }

  const {
    score,
    grade,
    documents_received,
    documents_required,
    missing_doc_types,
    categories_total,
    categories_reconciled,
    reconciliation_pct,
    unresolved_variances,
  } = data;

  const colors = gradeColor(grade);
  const barColor = scoreBarColor(score);
  const hasMissing = missing_doc_types.length > 0;
  const IconComponent = score >= 75 ? ShieldCheck : ShieldAlert;

  return (
    <div className="rounded-xl shadow-sm p-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg" style={{ backgroundColor: colors.bg }}>
            <IconComponent className="w-4 h-4" style={{ color: colors.text }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Data Quality</h3>
        </div>
        <span
          className="text-xs font-bold px-2 py-0.5 rounded-full"
          style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
        >
          {grade}
        </span>
      </div>

      {/* Score bar */}
      <div className="mb-3">
        <div className="flex justify-between text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}>
          <span>Overall score</span>
          <span className="font-medium font-mono" style={{ color: 'var(--color-text-primary)' }}>{score}</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <div
            className="h-full rounded-full"
            style={{ width: `${score}%`, backgroundColor: barColor }}
          />
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="text-center p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <p className="text-xs mb-0.5" style={{ color: 'var(--color-text-muted)' }}>Docs</p>
          <p className="text-sm font-semibold font-mono" style={{ color: 'var(--color-text-primary)' }}>
            {documents_received}<span className="font-normal" style={{ color: 'var(--color-text-dim)' }}>/{documents_required}</span>
          </p>
        </div>
        <div className="text-center p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <p className="text-xs mb-0.5" style={{ color: 'var(--color-text-muted)' }}>Recon</p>
          <p className="text-sm font-semibold font-mono" style={{ color: 'var(--color-text-primary)' }}>{reconciliation_pct}%</p>
        </div>
        <div className="text-center p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <p className="text-xs mb-0.5" style={{ color: 'var(--color-text-muted)' }}>Variances</p>
          <p
            className="text-sm font-semibold font-mono"
            style={{ color: unresolved_variances > 0 ? 'var(--color-blocker)' : 'var(--color-text-primary)' }}
          >
            {unresolved_variances}
          </p>
        </div>
      </div>

      {/* Recon detail */}
      {categories_total > 0 && (
        <p className="text-xs mb-2" style={{ color: 'var(--color-text-dim)' }}>
          {categories_reconciled} of {categories_total} categories approved
        </p>
      )}

      {/* Missing docs (collapsible) */}
      {hasMissing && (
        <div>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between text-xs rounded-lg px-3 py-2 transition-colors"
            style={{ color: 'var(--color-blocker)', backgroundColor: 'var(--color-blocker-tint)', border: '1px solid var(--color-blocker-tint-bd)' }}
          >
            <span className="flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              {missing_doc_types.length} missing doc{missing_doc_types.length !== 1 ? 's' : ''}
            </span>
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {expanded && (
            <ul className="mt-2 space-y-1">
              {missing_doc_types.map((t, i) => (
                <li key={i} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  <FileText className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
                  {t.replace(/_/g, ' ')}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
